import arriereBackoffice, {
  AirbnbReservationAlterationStatus,
  BookingHistorySnapshotKind,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { paymentSourceFragment } from '@arriere/modules/payments'

const requestBreakdownFragment = {
  amounts: {
    approved: true,
    refundedAt: true,
    refundSource: {
      affirm: true,
      bankLast4: true,
      cardLast4: true,
      credits: true,
      manual: true,
      platform: true,
      processType: true,
    },
    requested: true,
  },
  status: true,
  statesHistory: {
    author: {
      name: true,
    },
    datetime: true,
    note: true,
    status: true,
  },
  zendeskTicketUrl: true,
}

const bookingItemInfoFragment = {
  date: true,
  isCancelled: true,
  kind: true,
  note: true,
  title: true,
  total: true,
}

const bookingHistoryDataFragment = {
  booking: {
    emails: true,
    fees: {
      taxFeeId: true,
      title: true,
      value: true,
    },
    guestName: true,
    guests: true,
    home: {
      id: true,
      name: true,
      unitNumber: true,
    },
    period: {
      end: true,
      start: true,
    },
    pets: true,
    phones: true,
    total: true,
  },
  cancellationPolicy: {
    category: {
      id: true,
      name: true,
    },
    upheldPeriod: {
      end: true,
      start: true,
    },
  },
  contractStatusChanged: true,
  guest: {
    email: true,
    name: true,
    personId: true,
    phone: true,
  },
  idVerificationStatusChanged: true,
  item: {
    adjustment: {
      amount: true,
      reason: true,
    },
    cancellation: {
      reason: true,
      time: true,
    },
    info: {
      balance: true,
      createdAt: true,
      isCancelled: true,
      kind: true,
      note: true,
      title: true,
      total: true,
    },
    itemId: true,
    refund: {
      reason: true,
      total: true,
    },
    request: requestBreakdownFragment,
  },
  items: {
    balance: true,
    items: bookingItemInfoFragment,
    changes: {
      added: { itemId: true, info: bookingItemInfoFragment },
      removed: { itemId: true, info: bookingItemInfoFragment },
      updated: {
        itemId: true,
        before: bookingItemInfoFragment,
        after: bookingItemInfoFragment,
      },
    },
  },
  operationsCase: {
    actionBy: true,
    completedAt: true,
    taskType: true,
    vendorName: true,
    workOrderId: true,
  },
  payment: {
    amount: true,
    paymentId: true,
    source: paymentSourceFragment,
    title: true,
  },
  paymentAudit: {
    auditNotes: true,
    followUpDate: true,
    fraudStatus: true,
    paymentNotes: true,
    paymentPlan: true,
    paymentPlanDate: true,
    status: true,
  },
  refund: {
    amount: true,
    description: true,
    id: true,
  },
  serviceRequest: {
    comment: true,
    data: {
      dates: true,
    },
    name: true,
    price: true,
    status: true,
  },
  status: true,
  tags: {
    tags: {
      appliedReason: true,
      tagId: true,
      tagName: true,
    },
  },
}

const listFragment = {
  author: {
    internal: true,
    name: true,
  },
  bookingId: true,
  data: bookingHistoryDataFragment,
  id: true,
  kind: true,
  previousData: bookingHistoryDataFragment,
  time: true,
}

export const getBookingHistory = async (bookingId: string) => {
  return arriereBackoffice.queries.bookingHistory
    .raw({
      __alias: 'getBookingHistory',
      __args: { bookingId },
      list: listFragment,
      previousBooking: {
        list: listFragment,
      },
    })
    .then(async ({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1].message)
      }

      return data
    })
}

const modificationFragment = {
  from: true,
  to: true,
}

export const bookingModificationHistory = async (bookingId: string) => {
  return arriereBackoffice.queries.airbnbDistribution
    .raw({
      __alias: 'getBookingModificationHistory',
      reservationAlterations: {
        getAlterationsForBooking: {
          __args: { bookingId },
          reservationAlterationId: true,
          bookingId: true,
          status: true,
          metadata: {
            changes: {
              numberOfAdults: modificationFragment,
              numberOfChildren: modificationFragment,
              numberOfInfants: modificationFragment,
              numberOfPets: modificationFragment,
            },
          },
          payload: {
            initiatedBy: true,
            startDate: true,
            endDate: true,
            guestDetails: {
              numberOfAdults: true,
              numberOfChildren: true,
              numberOfInfants: true,
              numberOfPets: true,
            },
          },
          createdBy: { kind: true, name: true },
          completedBy: { kind: true, name: true },
          createdAt: true,
          completedAt: true,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1].message)
      }

      return data.reservationAlterations.getAlterationsForBooking
        ?.filter(alteration => alteration.status === AirbnbReservationAlterationStatus.accepted)
        .map(modification => ({
          id: modification.reservationAlterationId,
          kind: BookingHistorySnapshotKind.reservationAltered,
          bookingId: modification.bookingId,
          time: modification.createdAt || '',
          author: {
            internal: modification.createdBy?.kind?.toLocaleLowerCase()?.includes('internal'),
            name: modification.createdBy?.name,
          },
          data: {
            booking: {
              metadata: {
                ...modification.metadata?.changes,
              },
              payload: {
                ...modification.payload,
              },
            },
          },
        }))
    })
}
